import { SelectionModel } from '@angular/cdk/collections';
import { NgClass } from '@angular/common';
import { Component, OnInit, ViewChild, inject } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import {
  IonButton,
  IonCheckbox,
  IonCol,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonRow,
} from '@ionic/angular/standalone';
import { finalize, of, switchMap, tap } from 'rxjs';
import { BANNER_TYPE } from 'src/app/components/banner/banner.constants';
import { DatePickerComponent } from 'src/app/components/shared/form/components/date-picker/date-picker.component';
import { FormInputFieldComponent } from 'src/app/components/shared/form/components/form-input-field/form-input-field.component';
import { FormTimePickerComponent } from 'src/app/components/shared/form/components/form-time-picker/form-time-picker.component';
import { ModalFrameComponent } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.component';
import { BUTTON_TYPES } from 'src/app/components/shared/modal/components/modal-frame/modal-frame.constants';
import { IMAGE_SRC } from 'src/app/components/shared/shared-constants';
import { TableComponent } from 'src/app/components/shared/table/components/table/table.component';
import { SpecialCharacters } from 'src/app/core/enums';
import {
  ActiveCarryOverLoads,
  UldCountModalData,
} from 'src/app/core/models/position-operations/carry-over-loads.model';
import { CarryOverLoadsUtils } from 'src/app/core/utils/carry-over-loads/carry-over-loads.utils';
import { CommonUtils } from 'src/app/core/utils/common/common.utils';
import { AddEditUldModalComponent } from '../../outbound-scan-gle/add-edit-uld-modal/add-edit-uld-modal.component';
import { SmartBagCheckComponentComponent } from '../smart-bag-check-component/smart-bag-check-component.component';
import {
  COLUMN_CONFIG,
  CONST,
  ColumnIds,
  ColumnText,
} from './uld-count-modal.constants';

@Component({
  selector: 'app-uld-count-modal',
  imports: [
    ModalFrameComponent,
    IonRow,
    IonCol,
    IonButton,
    DatePickerComponent,
    FormTimePickerComponent,
    FormInputFieldComponent,
    ReactiveFormsModule,
    TableComponent,
    IonItem,
    IonCheckbox,
    IonLabel,
    NgClass,
    IonRadioGroup,
    IonRadio,
    FormsModule,
  ],
  templateUrl: './uld-count-modal.component.html',
  styleUrl: './uld-count-modal.component.scss',
})
export class UldCountModalComponent implements OnInit {
  @ViewChild('childTable') childTable!: TableComponent; // Reference to the child component

  private readonly _data: UldCountModalData = inject(MAT_DIALOG_DATA);
  private readonly _dialogRef = inject(MatDialogRef<UldCountModalComponent>);
  private readonly _dialog = inject(MatDialog);

  readonly commonUtils = inject(CommonUtils);
  readonly carryOverLoadsUtils = inject(CarryOverLoadsUtils);
  readonly fb = inject(FormBuilder);

  readonly SpecialCharacters = SpecialCharacters;
  readonly SRC = IMAGE_SRC;

  readonly ColumnIds = ColumnIds;
  readonly displayedColumn = Object.values(ColumnIds);
  readonly ColumnText = ColumnText;
  readonly COLUMN_CONFIG = COLUMN_CONFIG;
  defaultCloseTime = this.getFormTime();

  tableData: ActiveCarryOverLoads[] = [];

  readonly CONST = {
    ...CONST,
  };
  readonly BUTTON_TYPES = BUTTON_TYPES;
  maxDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() + 3,
  );
  minDate: Date = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    new Date().getDate() - 1,
  );
  closeDate = new Date();

  uldCountForm = new FormGroup({
    closeDate: new FormControl(new Date(), Validators.required),
    closeTime: new FormControl('', Validators.required),
    percentFull: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.min(0),
        Validators.max(100),
      ]),
    ),
  });

  selection = new SelectionModel<ActiveCarryOverLoads>(true, []);
  selectedUldNumberIndex: number | null = null;

  ngOnInit() {
    this.tableData = [this._data, ...this._data.childLoads];
    this.selectedUldNumberIndex =
      this._data.selectedUldNumberIndex !== null ? 0 : null;
  }

  onClose() {
    this._dialogRef.close();
  }

  /**
   * get form time on current date
   *
   * @memberof InterceptSummaryComponent
   */
  getFormTime() {
    const currentDate = new Date();
    const hours = currentDate.getHours().toString();
    const minutes = currentDate.getMinutes().toString();
    const tranformHours = hours.length > 1 ? hours : '0' + hours;
    const tranformMinutes = minutes.length > 1 ? minutes : '0' + minutes;

    return `${tranformHours}:${tranformMinutes}`;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.childTable.dataSource.data.length;
    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected()
      ? this.selection.clear()
      : (this.childTable.dataSource.data as ActiveCarryOverLoads[]).forEach(
          (row: ActiveCarryOverLoads) => this.selection.select(row),
        );
  }

  submit() {
    const tableData = this.childTable.dataSource.data as ActiveCarryOverLoads[];
    const payload = [
      {
        ...this._data,
        childLoads: this.selection.selected,
      },
    ] as ActiveCarryOverLoads[];
    const closeDate = this.uldCountForm.get('closeDate')?.value || new Date();
    const closeTime = this.uldCountForm.get('closeTime')?.value ?? '';
    const smartBagCheck = this._data.smartBag ?? false;

    const performClose = (checkSmartBag: boolean) =>
      this.carryOverLoadsUtils.closeTheLoad(
        payload,
        closeDate,
        closeTime,
        this.uldCountForm,
        0,
        checkSmartBag,
      );

    performClose(smartBagCheck)
      .pipe(
        switchMap((resp) => {
          if (resp.code !== 200) return of(null);

          // Scenario 1: SmartBagCheck is false originally which means it is either an international load or location has autoLink disabled
          if (!smartBagCheck) {
            this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
            this.commonUtils.updateLoadingModal(true);
          }

          // Scenario 2: SmartBagCheck is true and bag data is present which means it is a domestic load and location has autoLink enabled
          if (resp.response?.[0]?.bag) {
            const clonedForm = this.fb.group({
              closeDate: [closeDate, Validators.required],
              closeTime: [closeTime, Validators.required],
              percentFull: [
                this.uldCountForm.get('percentFull')?.value,
                [Validators.required, Validators.min(0), Validators.max(100)],
              ],
            });

            this._dialog
              .open(SmartBagCheckComponentComponent, {
                data: {
                  selectedLoad: this._data,
                  response: resp.response,
                  carryLoadsForm: clonedForm,
                },
              })
              .afterClosed()
              .subscribe((data: boolean) => {
                if (data) {
                  tableData.splice(1, 1);
                  this.childTable.dataSource.data = [...tableData];
                  this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                  this._dialogRef.close('close');
                }
              });

            return of(null);
          }

          // Scenario 3: SmartBagCheck is true but no bag data, retry with check disabled
          return of(null).pipe(
            tap(() => {
              this.commonUtils.updateLoadingModal(true);
              this.commonUtils.lockUpdateLoadingModal(true);
            }),
            switchMap(() => performClose(false)),
            switchMap((retryResp) => {
              if (retryResp.code === 200) {
                tableData.splice(1, 1);
                this.childTable.dataSource.data = [...tableData];
                this.commonUtils.updateBannerType(BANNER_TYPE.API_SUCCESS);
                this._dialogRef.close('close');
              }
              return of(null);
            }),
          );
        }),
        finalize(() => {
          this.commonUtils.updateLoadingModal(false);
          this.commonUtils.lockUpdateLoadingModal(false);
        }),
      )
      .subscribe();
  }

  loadKeyUpdate() {
    const selectedLoad = this.selection.selected[0];
    const origSort =
      selectedLoad.origSort.length == 2
        ? selectedLoad.origSort?.[0]
        : selectedLoad.origSort;

    const isMultipleSelection =
      this.selectedUldNumberIndex !== null ||
      this.selection.selected.length > 1;

    this._dialog
      .open(AddEditUldModalComponent, {
        width: '34.25rem',
        disableClose: true,
        autoFocus: false,
        data: {
          formData: {
            sortDate: this.uldCountForm.value.closeDate,
            sort: origSort,
            equipmentNumber: isMultipleSelection
              ? null
              : selectedLoad.uldNumber,
            equipmentType: isMultipleSelection ? null : selectedLoad.uldType,
            loadKey: selectedLoad.loadKey,
          },
          currentStep: 1,
          isLoadKeyUpdate: true,
          selectedLoad: isMultipleSelection ? this._data : selectedLoad,
          childLoadKey: true,
          childLoadWithParent: isMultipleSelection,
          selectedParentLoad:
            this.selectedUldNumberIndex !== null ? this._data : null,
          selectedChildLoads: this.selection.selected,
          isMultipleSelection,
        },
      })
      .afterClosed()
      .subscribe((resp) => {
        if (resp?.updateUld) {
          this._dialogRef.close('close');
        }
      });
  }
}
